import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  TextT,
  Image,
  Square,
  Circle,
  Minus,
  Download,
  Trash,
  ArrowClockwise,
  ArrowCounterClockwise,
  Copy,
  Cursor,
  MagnifyingGlassPlus,
  MagnifyingGlassMinus,
  Plus,
  X,
  InstagramLogo,
  FacebookLogo,
  Sparkle,
  Spinner,
  PaintBrush,
  Camera,
  Cube,
  Drop,
  Lightning as LightningIcon,
  Flower,
  FilmStrip,
  Palette,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VisualEditor = () => {
  const canvasRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [activeTool, setActiveTool] = useState("select");
  const [zoom, setZoom] = useState(100);
  const [rightTab, setRightTab] = useState("templates");
  const [canvasSize, setCanvasSize] = useState({ width: 1080, height: 1080 });
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // AI Image Gen state
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiStyle, setAiStyle] = useState("auto");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);

  useEffect(() => {
    const initCanvas = async () => {
      try {
        const fabricModule = await import("fabric");
        const fabric = fabricModule.default || fabricModule;

        if (canvasRef.current && !canvas) {
          const newCanvas = new fabric.Canvas(canvasRef.current, {
            width: canvasSize.width,
            height: canvasSize.height,
            backgroundColor: "#1a1a1a",
            selection: true,
          });

          newCanvas.on("selection:created", (e) =>
            setSelectedObject(e.selected?.[0] || null)
          );
          newCanvas.on("selection:updated", (e) =>
            setSelectedObject(e.selected?.[0] || null)
          );
          newCanvas.on("selection:cleared", () => setSelectedObject(null));

          setCanvas(newCanvas);
        }
      } catch (error) {
        console.error("Error initializing canvas:", error);
      }
    };
    initCanvas();
    return () => {
      if (canvas) canvas.dispose();
    };
  }, []);

  const saveHistory = useCallback(() => {
    if (!canvas) return;
    const json = canvas.toJSON();
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(json);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [canvas, history, historyIndex]);

  const undo = () => {
    if (historyIndex > 0 && canvas) {
      const prevState = history[historyIndex - 1];
      canvas.loadFromJSON(prevState).then(() => {
        canvas.renderAll();
        setHistoryIndex(historyIndex - 1);
      });
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1 && canvas) {
      const nextState = history[historyIndex + 1];
      canvas.loadFromJSON(nextState).then(() => {
        canvas.renderAll();
        setHistoryIndex(historyIndex + 1);
      });
    }
  };

  const addText = async () => {
    if (!canvas) return;
    try {
      const fabricModule = await import("fabric");
      const fabric = fabricModule.default || fabricModule;
      const text = new fabric.IText("Texto de ejemplo", {
        left: canvasSize.width / 2,
        top: canvasSize.height / 2,
        originX: "center",
        originY: "center",
        fontSize: 48,
        fill: "#ffffff",
        fontFamily: "Arial",
        fontWeight: "bold",
      });
      canvas.add(text);
      canvas.setActiveObject(text);
      canvas.renderAll();
      saveHistory();
    } catch (error) {
      console.error("Error adding text:", error);
    }
  };

  const addShape = async (type) => {
    if (!canvas) return;
    try {
      const fabricModule = await import("fabric");
      const fabric = fabricModule.default || fabricModule;
      let shape;
      const cx = canvasSize.width / 2;
      const cy = canvasSize.height / 2;

      if (type === "rect") {
        shape = new fabric.Rect({
          left: cx, top: cy, originX: "center", originY: "center",
          width: 150, height: 150, fill: "#3b82f6", rx: 10, ry: 10,
        });
      } else if (type === "circle") {
        shape = new fabric.Circle({
          left: cx, top: cy, originX: "center", originY: "center",
          radius: 75, fill: "#10b981",
        });
      } else if (type === "line") {
        shape = new fabric.Line([cx - 100, cy, cx + 100, cy], {
          stroke: "#ffffff", strokeWidth: 4,
        });
      }
      if (shape) {
        canvas.add(shape);
        canvas.setActiveObject(shape);
        canvas.renderAll();
        saveHistory();
      }
    } catch (error) {
      console.error("Error adding shape:", error);
    }
  };

  const addImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file && canvas) {
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const fabricModule = await import("fabric");
            const fabric = fabricModule.default || fabricModule;
            const img = await fabric.FabricImage.fromURL(event.target.result);
            img.scaleToWidth(Math.min(400, canvasSize.width * 0.6));
            img.set({
              left: canvasSize.width / 2,
              top: canvasSize.height / 2,
              originX: "center",
              originY: "center",
            });
            canvas.add(img);
            canvas.setActiveObject(img);
            canvas.renderAll();
            saveHistory();
          } catch (error) {
            console.error("Error adding image:", error);
          }
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const addImageFromBase64 = async (base64Data) => {
    if (!canvas) return;
    try {
      const fabricModule = await import("fabric");
      const fabric = fabricModule.default || fabricModule;
      const dataUrl = `data:image/png;base64,${base64Data}`;
      const img = await fabric.FabricImage.fromURL(dataUrl);
      // Scale to fit canvas
      const scale = Math.min(
        canvasSize.width / img.width,
        canvasSize.height / img.height,
        1
      );
      img.scale(scale);
      img.set({
        left: canvasSize.width / 2,
        top: canvasSize.height / 2,
        originX: "center",
        originY: "center",
      });
      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
      saveHistory();
      toast.success("Imagen agregada al canvas");
    } catch (error) {
      console.error("Error adding AI image:", error);
      toast.error("Error al agregar imagen");
    }
  };

  const setAsBackground = async (base64Data) => {
    if (!canvas) return;
    try {
      const fabricModule = await import("fabric");
      const fabric = fabricModule.default || fabricModule;
      const dataUrl = `data:image/png;base64,${base64Data}`;
      const img = await fabric.FabricImage.fromURL(dataUrl);
      const scaleX = canvasSize.width / img.width;
      const scaleY = canvasSize.height / img.height;
      const scale = Math.max(scaleX, scaleY);
      img.scale(scale);
      img.set({
        left: canvasSize.width / 2,
        top: canvasSize.height / 2,
        originX: "center",
        originY: "center",
        selectable: false,
        evented: false,
      });
      // Move to back
      canvas.insertAt(img, 0);
      canvas.renderAll();
      saveHistory();
      toast.success("Fondo aplicado");
    } catch (error) {
      console.error("Error setting background:", error);
      toast.error("Error al aplicar fondo");
    }
  };

  const deleteSelected = () => {
    if (!canvas || !selectedObject) return;
    canvas.remove(selectedObject);
    setSelectedObject(null);
    canvas.renderAll();
    saveHistory();
  };

  const duplicateSelected = async () => {
    if (!canvas || !selectedObject) return;
    try {
      const cloned = await selectedObject.clone();
      cloned.set({ left: (cloned.left || 0) + 20, top: (cloned.top || 0) + 20 });
      canvas.add(cloned);
      canvas.setActiveObject(cloned);
      canvas.renderAll();
      saveHistory();
    } catch (error) {
      console.error("Error duplicating:", error);
    }
  };

  const exportCanvas = (format = "png") => {
    if (!canvas) return;
    const dataURL = canvas.toDataURL({ format, quality: 1, multiplier: 2 });
    const link = document.createElement("a");
    link.download = `design.${format}`;
    link.href = dataURL;
    link.click();
    toast.success("Imagen exportada");
  };

  const changeCanvasSize = (preset) => {
    const sizes = {
      instagram: { width: 1080, height: 1080 },
      instagramStory: { width: 1080, height: 1920 },
      facebook: { width: 1200, height: 630 },
      twitter: { width: 1200, height: 675 },
      linkedin: { width: 1200, height: 627 },
    };
    const size = sizes[preset];
    if (size && canvas) {
      setCanvasSize(size);
      canvas.setWidth(size.width);
      canvas.setHeight(size.height);
      canvas.renderAll();
    }
  };

  // AI Image Generation
  const generateAIImage = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Escribe una descripción para la imagen");
      return;
    }
    setAiGenerating(true);
    setGeneratedImage(null);
    try {
      const response = await axios.post(
        `${API}/platform/editor/generate-image`,
        { prompt: aiPrompt, style: aiStyle },
        { withCredentials: true, timeout: 120000 }
      );
      if (response.data.success) {
        setGeneratedImage(response.data.image_base64);
        toast.success("Imagen generada con IA");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al generar imagen";
      toast.error(msg);
    } finally {
      setAiGenerating(false);
    }
  };

  const applyTemplate = async (template) => {
    if (!canvas) return;
    try {
      const fabricModule = await import("fabric");
      const fabric = fabricModule.default || fabricModule;
      canvas.clear();
      canvas.backgroundColor = template.background;
      if (template.elements) {
        for (const el of template.elements) {
          if (el.type === "text") {
            canvas.add(new fabric.IText(el.content, {
              left: el.left, top: el.top, fontSize: el.fontSize, fill: el.fill,
              fontFamily: el.fontFamily || "Arial", fontWeight: el.fontWeight || "normal",
              textAlign: el.textAlign || "left",
            }));
          } else if (el.type === "rect") {
            canvas.add(new fabric.Rect({
              left: el.left, top: el.top, width: el.width, height: el.height,
              fill: el.fill, rx: el.rx || 0, ry: el.ry || 0,
            }));
          } else if (el.type === "circle") {
            canvas.add(new fabric.Circle({
              left: el.left, top: el.top, radius: el.radius, fill: el.fill,
            }));
          }
        }
      }
      canvas.renderAll();
      saveHistory();
      toast.success("Plantilla aplicada");
    } catch (error) {
      console.error("Error applying template:", error);
    }
  };

  const aiStyles = [
    { id: "auto", label: "Auto", icon: Sparkle },
    { id: "fotorrealista", label: "Foto", icon: Camera },
    { id: "ilustracion", label: "Ilustración", icon: PaintBrush },
    { id: "3d", label: "3D", icon: Cube },
    { id: "minimalista", label: "Minimal", icon: Minus },
    { id: "neon", label: "Neon", icon: LightningIcon },
    { id: "acuarela", label: "Acuarela", icon: Drop },
    { id: "vintage", label: "Vintage", icon: FilmStrip },
    { id: "flat", label: "Flat", icon: Palette },
  ];

  const templates = [
    {
      id: "promo1", name: "Promoción",
      preview: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      background: "#667eea",
      elements: [
        { type: "text", content: "50% OFF", left: 340, top: 400, fontSize: 120, fill: "#ffffff", fontWeight: "bold" },
        { type: "text", content: "Solo por tiempo limitado", left: 280, top: 550, fontSize: 36, fill: "#ffffff" },
        { type: "rect", left: 340, top: 700, width: 400, height: 60, fill: "#ffffff", rx: 30 },
        { type: "text", content: "COMPRAR AHORA", left: 400, top: 715, fontSize: 24, fill: "#667eea", fontWeight: "bold" },
      ],
    },
    {
      id: "minimal1", name: "Minimalista",
      preview: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
      background: "#1a1a2e",
      elements: [
        { type: "text", content: "NUEVO", left: 100, top: 200, fontSize: 24, fill: "#64748b" },
        { type: "text", content: "Producto\nIncreíble", left: 100, top: 350, fontSize: 72, fill: "#ffffff", fontWeight: "bold" },
        { type: "text", content: "Descubre más →", left: 100, top: 600, fontSize: 24, fill: "#3b82f6" },
      ],
    },
    {
      id: "vibrant1", name: "Vibrante",
      preview: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      background: "#f5576c",
      elements: [
        { type: "circle", left: -200, top: -200, radius: 400, fill: "#f093fb" },
        { type: "text", content: "¡OFERTA!", left: 340, top: 300, fontSize: 96, fill: "#ffffff", fontWeight: "bold" },
        { type: "text", content: "Hasta 70% de descuento", left: 260, top: 450, fontSize: 40, fill: "#ffffff" },
      ],
    },
    {
      id: "elegant1", name: "Elegante",
      preview: "linear-gradient(135deg, #0c0c0c 0%, #1a1a1a 100%)",
      background: "#0c0c0c",
      elements: [
        { type: "text", content: "LUXURY", left: 340, top: 450, fontSize: 80, fill: "#d4af37", fontWeight: "bold" },
        { type: "text", content: "Collection 2025", left: 380, top: 560, fontSize: 32, fill: "#ffffff" },
      ],
    },
  ];

  const sizePresets = [
    { id: "instagram", name: "Instagram Post", size: "1080x1080", icon: InstagramLogo },
    { id: "instagramStory", name: "Instagram Story", size: "1080x1920", icon: InstagramLogo },
    { id: "facebook", name: "Facebook Post", size: "1200x630", icon: FacebookLogo },
  ];

  const quickPrompts = [
    "Fondo abstracto con gradientes vibrantes para post de redes sociales",
    "Paisaje minimalista con montañas al atardecer",
    "Textura de mármol elegante en tonos oscuros",
    "Patrón geométrico moderno con colores neón",
    "Fondo con flores tropicales para story de Instagram",
    "Skyline urbano nocturno con luces de ciudad",
  ];

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="visual-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-medium text-white">Editor Visual</h1>
          <p className="text-zinc-500 text-sm">Crea diseños profesionales con IA generativa</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportCanvas("png")}
            className="px-4 py-2 bg-white text-black rounded-lg font-medium text-sm flex items-center gap-2 hover:bg-zinc-200 transition-colors"
            data-testid="export-button"
          >
            <Download size={18} weight="bold" />
            Exportar PNG
          </button>
        </div>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left Toolbar */}
        <div className="w-16 bg-[#13151A] border border-zinc-800 rounded-lg p-2 flex flex-col gap-1">
          <ToolBtn active={activeTool === "select"} onClick={() => setActiveTool("select")} title="Seleccionar" testId="tool-select">
            <Cursor size={20} />
          </ToolBtn>
          <ToolBtn onClick={addText} title="Texto" testId="add-text-button">
            <TextT size={20} />
          </ToolBtn>
          <ToolBtn onClick={addImage} title="Imagen" testId="add-image-button">
            <Image size={20} />
          </ToolBtn>
          <ToolBtn onClick={() => addShape("rect")} title="Rectángulo" testId="add-rect-button">
            <Square size={20} />
          </ToolBtn>
          <ToolBtn onClick={() => addShape("circle")} title="Círculo">
            <Circle size={20} />
          </ToolBtn>
          <ToolBtn onClick={() => addShape("line")} title="Línea">
            <Minus size={20} />
          </ToolBtn>

          <div className="my-2 border-t border-zinc-800" />

          {/* AI Button */}
          <ToolBtn
            active={rightTab === "ai"}
            onClick={() => setRightTab("ai")}
            title="Generar con IA"
            testId="ai-image-tool"
            className="relative"
          >
            <Sparkle size={20} weight="fill" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          </ToolBtn>

          <div className="flex-1" />

          <ToolBtn onClick={undo} disabled={historyIndex <= 0} title="Deshacer">
            <ArrowCounterClockwise size={20} />
          </ToolBtn>
          <ToolBtn onClick={redo} disabled={historyIndex >= history.length - 1} title="Rehacer">
            <ArrowClockwise size={20} />
          </ToolBtn>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 bg-[#0A0A0A] border border-zinc-800 rounded-lg overflow-auto flex items-center justify-center p-8">
          <div style={{ transform: `scale(${zoom / 100})`, transformOrigin: "center" }}>
            <canvas ref={canvasRef} id="design-canvas" className="shadow-2xl" style={{ border: "1px solid #333" }} />
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-80 bg-[#13151A] border border-zinc-800 rounded-lg overflow-hidden flex flex-col">
          {/* Tabs */}
          <div className="flex border-b border-zinc-800">
            {[
              { id: "templates", label: "Plantillas" },
              { id: "ai", label: "IA Generativa" },
              { id: "properties", label: "Propiedades" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setRightTab(tab.id)}
                className={`flex-1 py-3 text-xs font-medium transition-colors ${
                  rightTab === tab.id
                    ? "text-white border-b-2 border-white"
                    : "text-zinc-500 hover:text-zinc-300"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {/* Templates Tab */}
            {rightTab === "templates" && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-widest mb-2 block">Tamaño</label>
                  <div className="space-y-2">
                    {sizePresets.map((preset) => {
                      const Icon = preset.icon;
                      return (
                        <button key={preset.id} onClick={() => changeCanvasSize(preset.id)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg border border-zinc-800 hover:border-zinc-600 transition-colors text-left">
                          <Icon size={20} className="text-zinc-400" />
                          <div>
                            <div className="text-sm text-white">{preset.name}</div>
                            <div className="text-xs text-zinc-500">{preset.size}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-widest mb-2 block">Plantillas</label>
                  <div className="grid grid-cols-2 gap-2">
                    {templates.map((template) => (
                      <button key={template.id} onClick={() => applyTemplate(template)}
                        className="aspect-square rounded-lg overflow-hidden border border-zinc-800 hover:border-zinc-600 transition-colors"
                        data-testid={`template-${template.id}`}>
                        <div className="w-full h-full flex items-center justify-center text-white text-xs font-medium"
                          style={{ background: template.preview }}>
                          {template.name}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* AI Generation Tab */}
            {rightTab === "ai" && (
              <div className="space-y-4" data-testid="ai-panel">
                {/* Style Selection */}
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-widest mb-2 block">Estilo</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {aiStyles.map((style) => {
                      const Icon = style.icon;
                      return (
                        <button
                          key={style.id}
                          onClick={() => setAiStyle(style.id)}
                          className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-xs transition-all ${
                            aiStyle === style.id
                              ? "bg-white text-black border-white"
                              : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                          }`}
                          data-testid={`ai-style-${style.id}`}
                        >
                          <Icon size={16} weight={aiStyle === style.id ? "fill" : "regular"} />
                          {style.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Prompt */}
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-widest mb-2 block">Descripción</label>
                  <textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Describe la imagen que quieres generar..."
                    rows={3}
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-zinc-800 rounded-lg text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-white resize-none"
                    data-testid="ai-prompt-input"
                  />
                </div>

                {/* Quick Prompts */}
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-widest mb-2 block">Ideas rápidas</label>
                  <div className="flex flex-wrap gap-1.5">
                    {quickPrompts.map((qp, i) => (
                      <button
                        key={i}
                        onClick={() => setAiPrompt(qp)}
                        className="text-xs px-2 py-1 rounded-full border border-zinc-800 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200 transition-colors truncate max-w-full"
                      >
                        {qp.length > 35 ? qp.slice(0, 35) + "..." : qp}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={generateAIImage}
                  disabled={aiGenerating || !aiPrompt.trim()}
                  className="w-full py-3 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white rounded-lg font-medium text-sm flex items-center justify-center gap-2 hover:from-violet-500 hover:to-fuchsia-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="generate-ai-image-button"
                >
                  {aiGenerating ? (
                    <>
                      <Spinner size={18} className="animate-spin" />
                      Generando... (30-60s)
                    </>
                  ) : (
                    <>
                      <Sparkle size={18} weight="fill" />
                      Generar Imagen con IA
                    </>
                  )}
                </button>

                {/* Generated Image Preview */}
                {generatedImage && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-2"
                    data-testid="ai-generated-preview"
                  >
                    <label className="text-xs text-zinc-500 uppercase tracking-widest block">Resultado</label>
                    <div className="relative rounded-lg overflow-hidden border border-zinc-700">
                      <img
                        src={`data:image/png;base64,${generatedImage}`}
                        alt="AI Generated"
                        className="w-full h-auto"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => addImageFromBase64(generatedImage)}
                        className="py-2 bg-white text-black rounded-lg text-xs font-medium hover:bg-zinc-200 transition-colors flex items-center justify-center gap-1"
                        data-testid="add-to-canvas-button"
                      >
                        <Plus size={14} weight="bold" />
                        Al Canvas
                      </button>
                      <button
                        onClick={() => setAsBackground(generatedImage)}
                        className="py-2 bg-zinc-800 text-white rounded-lg text-xs font-medium hover:bg-zinc-700 transition-colors flex items-center justify-center gap-1"
                        data-testid="set-as-background-button"
                      >
                        <Image size={14} />
                        Como Fondo
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Loading Indicator */}
                {aiGenerating && (
                  <div className="bg-[#0A0A0A] border border-zinc-800 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-500 flex items-center justify-center">
                        <Sparkle size={16} weight="fill" className="text-white animate-pulse" />
                      </div>
                      <div>
                        <div className="text-sm text-white font-medium">GPT Image 1</div>
                        <div className="text-xs text-zinc-500">Creando tu imagen...</div>
                      </div>
                    </div>
                    <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-violet-600 to-fuchsia-500 rounded-full"
                        initial={{ width: "0%" }}
                        animate={{ width: "90%" }}
                        transition={{ duration: 50, ease: "linear" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Properties Tab */}
            {rightTab === "properties" && (
              <div className="space-y-4">
                {selectedObject ? (
                  <>
                    <div className="flex gap-2">
                      <button onClick={duplicateSelected}
                        className="flex-1 py-2 px-3 bg-zinc-800 text-zinc-300 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-zinc-700 transition-colors">
                        <Copy size={16} /> Duplicar
                      </button>
                      <button onClick={deleteSelected}
                        className="py-2 px-3 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                        data-testid="delete-object-button">
                        <Trash size={16} />
                      </button>
                    </div>
                    {selectedObject.fill !== undefined && (
                      <div>
                        <label className="text-xs text-zinc-500 uppercase tracking-widest mb-2 block">Color</label>
                        <div className="flex gap-2 flex-wrap">
                          {["#ffffff", "#000000", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#d4af37"].map(
                            (color) => (
                              <button key={color} onClick={() => { selectedObject.set("fill", color); canvas.renderAll(); saveHistory(); }}
                                className="w-7 h-7 rounded-lg border border-zinc-700 hover:scale-110 transition-transform"
                                style={{ backgroundColor: color }} />
                            )
                          )}
                        </div>
                      </div>
                    )}
                    {selectedObject.type === "i-text" && (
                      <div>
                        <label className="text-xs text-zinc-500 uppercase tracking-widest mb-2 block">
                          Tamaño: {selectedObject.fontSize}px
                        </label>
                        <input type="range" min="12" max="200" value={selectedObject.fontSize || 48}
                          onChange={(e) => { selectedObject.set("fontSize", parseInt(e.target.value)); canvas.renderAll(); }}
                          className="w-full" />
                      </div>
                    )}
                    {/* Opacity */}
                    <div>
                      <label className="text-xs text-zinc-500 uppercase tracking-widest mb-2 block">
                        Opacidad: {Math.round((selectedObject.opacity || 1) * 100)}%
                      </label>
                      <input type="range" min="0" max="100" value={Math.round((selectedObject.opacity || 1) * 100)}
                        onChange={(e) => { selectedObject.set("opacity", parseInt(e.target.value) / 100); canvas.renderAll(); }}
                        className="w-full" />
                    </div>
                  </>
                ) : (
                  <div className="text-center text-zinc-500 py-8">
                    <Cursor size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Selecciona un elemento para editar</p>
                  </div>
                )}
                {/* Zoom */}
                <div className="pt-4 border-t border-zinc-800">
                  <label className="text-xs text-zinc-500 uppercase tracking-widest mb-2 block">Zoom: {zoom}%</label>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setZoom(Math.max(25, zoom - 25))} className="p-2 bg-zinc-800 rounded-lg text-zinc-400 hover:bg-zinc-700">
                      <MagnifyingGlassMinus size={16} />
                    </button>
                    <input type="range" min="25" max="200" value={zoom} onChange={(e) => setZoom(parseInt(e.target.value))} className="flex-1" />
                    <button onClick={() => setZoom(Math.min(200, zoom + 25))} className="p-2 bg-zinc-800 rounded-lg text-zinc-400 hover:bg-zinc-700">
                      <MagnifyingGlassPlus size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Tool Button
const ToolBtn = ({ children, active, onClick, disabled, title, testId, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-3 rounded-lg transition-colors ${
      active ? "bg-white text-black" : "text-zinc-400 hover:bg-zinc-800"
    } disabled:opacity-30 ${className}`}
    data-testid={testId}
  >
    {children}
  </button>
);

export default VisualEditor;
